import dynamic from "next/dynamic";
import React, {useEffect, useState} from 'react';
import {Menu, Search, User, X} from 'lucide-react';
import Link from 'next/link';
import {Badge} from "@/components/ui/badge";
import Image from "next/image";
import {navigationConfig} from "@/components/NavBar";
import {useStoredCart} from "@/hooks/useStoredCart";
import {usePathname, useRouter, useSearchParams} from "next/navigation";
import Cookies from "js-cookie";
import {CONTACT_PHONE_HREF} from "@/lib/public-config";

const DeferredFormLead = dynamic(() => import("@/components/FormLead"), { ssr: false });
const DeferredSearchBoxHome = dynamic(() => import("@/components/Home/SearchBoxhome"), { ssr: false });

export interface NavbarMobileProps {
    isScrolled: boolean;
}

const NavbarMobile = ({isScrolled}: NavbarMobileProps) => {
    const [menuOpen, setMenuOpen] = useState(false);
    const [searchOpen, setSearchOpen] = useState(false);
    const { count: cartCount } = useStoredCart();
    const [leadFrom, setLeadFrom] = useState(false);
    const [telephone, setTelephone] = useState(false);
    const [isClient, setIsClient] = useState(false);
    const [userData, setUserData] = useState<any>(null);
    const router = useRouter();
    const pathname = usePathname();
    const searchParams = useSearchParams();
    const currentOrder = searchParams?.get("order") || "";

    useEffect(() => {
        setIsClient(true);
        try {
            const raw = Cookies.get("userData");
            if (raw) setUserData(JSON.parse(raw));
        } catch {}
    }, []);

    const handleAccount = () => {
        setMenuOpen(false);
        setSearchOpen(false);

        if (pathname === '/diventa-partner-commercianti' || pathname === '/diventa-partner') {
            window.location.href = 'https://www.consorzioauto.com/';
            return;
        }

        if (userData?.code) {
            window.location.href = 'https://consorzioauto.com/dashboard';
            return;
        }

        if (userData) {
            router.push('/account/attivita');
        } else {
            router.push('/auth/accedi');
        }
    };

    // Gestione dello scroll del body quando il menu è aperto
    useEffect(() => {
        if (menuOpen) {
            // Disattiva lo scroll
            document.body.style.overflow = 'hidden';
            document.body.style.paddingRight = '0px'; // Evita il jump dello scrollbar
        } else {
            // Riattiva lo scroll
            document.body.style.overflow = 'unset';
            document.body.style.paddingRight = '0px';
        }

        // Cleanup quando il componente viene smontato
        return () => {
            document.body.style.overflow = 'unset';
            document.body.style.paddingRight = '0px';
        };
    }, [menuOpen]);

    const MobileDynamicDropdown = ({title, items}: any) => {
        const [isOpen, setIsOpen] = useState(false);

        const handleToggle = () => {
            setIsOpen(!isOpen);
        };

        return (
            <div className="w-full">
                {/* Trigger del menu mobile */}
                <button
                    onClick={handleToggle}
                    className="w-full text-center flex items-center justify-center gap-x-4 px-4 py-4 text-lg font-medium text-gray-800 hover:bg-gray-50 transition-colors duration-200"
                >
                    <span>{title}</span>
                    <svg
                        className={`w-5 h-5 transform transition-transform duration-200 ${isOpen ? 'rotate-180' : ''}`}
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                    >
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7"/>
                    </svg>
                </button>

                {/* Sottomenu mobile */}
                {isOpen && (
                    <div className="">
                        {items.map((item: any, index: any) => (
                            <div key={index}>
                                {item.isExternal ? (
                                    // Link esterno
                                    <a
                                        href={item.href}
                                        target={item.target || "_blank"}
                                        rel={item.rel || "noopener noreferrer"}
                                        className="flex items-center justify-center px-8 py-3 text-base text-gray-600 hover:bg-gray-100 hover:text-gray-900 transition-colors duration-200"
                                        onClick={() => setMenuOpen(false)}
                                    >
                                        <span>{item.label}</span>
                                        {item.isExternal && (
                                            <svg className="w-4 h-4 ml-2" fill="none" stroke="currentColor"
                                                 viewBox="0 0 24 24">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                                                      d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14"/>
                                            </svg>
                                        )}
                                    </a>
                                ) : (
                                    // Link interno
                                    <Link
                                        hrefLang={item.hrefLang || 'it'}
                                        href={item.href}
                                        className="block px-8 py-3 text-base text-center text-gray-600 hover:bg-gray-100 hover:text-gray-900 transition-colors duration-200"
                                        onClick={() => setMenuOpen(false)}
                                    >
                                        {item.label}
                                    </Link>
                                )}
                                {/* Separatore opzionale */}
                                {item.separator && index < items.length - 1 && (
                                    <hr className="border-gray-200"/>
                                )}
                            </div>
                        ))}
                    </div>
                )}
            </div>
        );
    };

    // Componente per link semplici mobile
    const MobileSimpleLink = ({href, label, hrefLang, className = ''}: any) => {
        const resolvedHref = href === "/parco-veicoli" && currentOrder
            ? `${href}?order=${encodeURIComponent(currentOrder)}`
            : href;

        return (
            <Link
                hrefLang={hrefLang || 'it'}
                href={resolvedHref}
                className={`block w-full text-center px-4 py-4 text-lg font-medium text-gray-800 hover:bg-gray-50 transition-colors duration-200 ${className}`}
                onClick={() => setMenuOpen(false)}
            >
                {label}
            </Link>
        );
    };

    return (
        <>
            {/* Header fisso */}
            <div className={`fixed top-0 left-0 w-full z-40 lg:hidden`}>
                <div
                    className={`flex justify-between items-center p-4 ${menuOpen || searchOpen ? 'bg-white' : isScrolled ? 'backdrop-blur-md bg-white/30' : ''}`}>
                    <div className="relative flex w-full items-center justify-between gap-3">
                        <Link href='/carrello' hrefLang={'it'} className="relative" aria-label="Vai al carrello">
                            <div className="relative">
                                <svg width="27" height="25" viewBox="0 0 27 25" fill="none"
                                     xmlns="http://www.w3.org/2000/svg">
                                    <path fillRule="evenodd" clipRule="evenodd"
                                          d="M0.0183924 1.34385C0.119968 0.835973 0.614029 0.506599 1.12191 0.608175L2.43404 0.870601C3.91609 1.16701 5.03084 2.3964 5.18123 3.9003L5.28784 4.9664H22.2628C24.7439 4.9664 26.5644 7.29808 25.9626 9.70512L24.5587 15.3208C23.9673 17.6865 21.8417 19.3461 19.4031 19.3461H8.15829C6.41766 19.3461 4.95155 18.0454 4.74416 16.3172L3.50691 6.00674L3.31492 4.08693C3.24656 3.40334 2.73986 2.84453 2.0662 2.70979L0.754069 2.44737C0.24619 2.34579 -0.0831833 1.85173 0.0183924 1.34385ZM9.69081 8.65748C9.2766 8.65748 8.94081 8.99327 8.94081 9.40748C8.94081 9.82169 9.2766 10.1575 9.69081 10.1575H14.6925C15.1067 10.1575 15.4425 9.82169 15.4425 9.40748C15.4425 8.99327 15.1067 8.65748 14.6925 8.65748H9.69081Z"
                                          fill="#000D7D"/>
                                    <circle cx="9.06543" cy="22.7847" r="1.87562" fill="#000D7D"/>
                                    <circle cx="20.3192" cy="22.7847" r="1.87562" fill="#000D7D"/>
                                </svg>
                                {isClient && cartCount > 0 && (
                                    <Badge className="absolute -top-2 -right-2 !px-2 text-white border-0">
                                        {cartCount}
                                    </Badge>
                                )}
                            </div>
                        </Link>

                        {isClient && (
                            <button
                                type="button"
                                title="Cerca auto"
                                aria-label={searchOpen ? "Chiudi ricerca auto" : "Apri ricerca auto"}
                                aria-expanded={searchOpen}
                                onClick={() => {
                                    setMenuOpen(false);
                                    setSearchOpen((open) => !open);
                                }}
                                className={`absolute left-10 top-1/2 -translate-y-1/2 rounded-full p-2 text-primary transition-colors ${searchOpen ? 'bg-primary/10' : ''}`}
                            >
                                <Search size={22}/>
                            </button>
                        )}

                        <Link
                            href="/"
                            hrefLang="it"
                            title="Gruppo Piccirillo - Auto di Lusso Nuove e Usate in Italia"
                            className="flex items-center"
                        >
                            <Image
                                src="/logo-vettoriale.svg"
                                alt="Gruppo Piccirillo logo"
                                width={48}
                                height={48}
                                priority={true}
                                style={{ width: "48px", height: "48px" }}
                            />
                        </Link>

                        <div className="flex items-center gap-1">
                            <button
                                type="button"
                                title={userData ? "Area riservata" : "Accedi"}
                                aria-label={userData ? "Apri area riservata" : "Vai al login"}
                                onClick={handleAccount}
                                className="rounded-full p-2 text-primary"
                            >
                                <User size={22} />
                            </button>

                            <button
                                title="bugermenu"
                                onClick={() => {
                                    setSearchOpen(false);
                                    setMenuOpen(!menuOpen);
                                }}
                                className={`menu__button cursor-pointer rounded-full p-2`}
                            >
                                {menuOpen ? <X className="text-primary" size={24}/> :
                                    <Menu className="text-primary" size={24}/>}
                            </button>
                        </div>
                    </div>
                </div>
            </div>

            {searchOpen && !menuOpen && (
                <div className="fixed left-0 right-0 top-20 z-40 px-4 pb-4 lg:hidden">
                    <div className="rounded-b-2xl bg-white/95 p-3 shadow-lg backdrop-blur-md">
                        <DeferredSearchBoxHome className="!py-1.5 shadow-none"/>
                    </div>
                </div>
            )}

            {/* Menu mobile fullscreen */}
            {menuOpen && (
                <div className="fixed inset-0 z-[39] bg-white lg:hidden">
                    {/* Contenitore principale con altezza 100svh */}
                    <div className="flex flex-col h-[100svh]">
                        {/* Spacer per l'header fisso */}
                        <div className="h-20"></div>

                        {/* Menu Links - area scrollabile */}
                        <div className="flex-1 overflow-y-auto">
                            <button
                                type="button"
                                onClick={handleAccount}
                                className="flex w-full items-center justify-center gap-2 border-b border-gray-100 px-4 py-4 text-center text-lg font-medium text-primary"
                            >
                                <User size={18} />
                                <span>{userData ? "Area riservata" : "Accedi"}</span>
                            </button>

                            {navigationConfig.map((item: any, index: any) => (
                                <div key={index}>
                                    {item.type === 'dropdown' ? (
                                        <MobileDynamicDropdown
                                            title={item.title}
                                            items={item.items}
                                        />
                                    ) : (
                                        <MobileSimpleLink
                                            href={item.href}
                                            label={item.label}
                                            hrefLang={item.hrefLang || 'it'}
                                            className={item.className}
                                        />
                                    )}
                                </div>
                            ))}
                        </div>

                        {/* Action Buttons - sempre visibili in fondo */}
                        <div className="bg-white border-t border-gray-100 px-4 py-6">
                            <div className="flex justify-center gap-8 items-center">
                                <div className="relative cursor-pointer" onClick={() => {
                                    setMenuOpen(false);
                                    setLeadFrom(true);
                                }}>
                                    <svg width="33" height="32" viewBox="0 0 33 32" fill="none" xmlns="http://www.w3.org/2000/svg">
                                        <path d="M24.1582 0.586121C28.9092 0.586121 32.7607 4.43768 32.7607 9.18866V18.1652C32.7606 22.9161 28.9091 26.7678 24.1582 26.7678H10.2471C9.82255 26.7678 9.41062 26.9118 9.0791 27.1769L4.84863 30.5607C3.13438 31.9321 0.595703 30.7121 0.595703 28.5168V9.18866C0.595703 4.43782 4.44648 0.586343 9.19727 0.586121H24.1582ZM9.27148 11.881C8.2387 11.881 7.40142 12.7184 7.40137 13.7512C7.40137 14.784 8.23866 15.6213 9.27148 15.6213C10.3041 15.621 11.1416 14.7838 11.1416 13.7512C11.1415 12.7185 10.3041 11.8813 9.27148 11.881ZM16.7529 11.881C15.7201 11.881 14.8829 12.7184 14.8828 13.7512C14.8828 14.784 15.7201 15.6213 16.7529 15.6213C17.7856 15.6211 18.623 14.7839 18.623 13.7512C18.623 12.7185 17.7856 11.8812 16.7529 11.881ZM24.2314 11.881C23.1987 11.881 22.3614 12.7184 22.3613 13.7512C22.3613 14.784 23.1986 15.6213 24.2314 15.6213C25.2642 15.6212 26.1016 14.7839 26.1016 13.7512C26.1015 12.7185 25.2641 11.8812 24.2314 11.881Z" fill="#0131B8"/>
                                    </svg>
                                </div>

                                <div className="relative cursor-pointer" onClick={() => {
                                    setMenuOpen(false);
                                    window.location.href = CONTACT_PHONE_HREF;
                                }}>
                                    <svg width="30" height="31" viewBox="0 0 30 31" fill="none" xmlns="http://www.w3.org/2000/svg">
                                        <path d="M26.6407 29.0514C23.4098 32.2823 15.1357 29.2465 8.15994 22.2707C1.18418 15.2949 -1.85164 7.02083 1.37923 3.78994L3.50651 1.66266C4.97509 0.194074 7.39496 0.232894 8.91143 1.74936L12.2064 5.04434C13.7229 6.56081 13.7617 8.98068 12.2931 10.4493L11.8362 10.9062C11.0433 11.6991 10.9657 12.9782 11.7025 13.871C12.4131 14.7322 13.1793 15.59 14.01 16.4207C14.8407 17.2514 15.6984 18.0175 16.5596 18.7282C17.4524 19.4649 18.7316 19.3874 19.5245 18.5945L19.9814 18.1375C21.45 16.669 23.8698 16.7078 25.3863 18.2242L28.6813 21.5192C30.1978 23.0357 30.2366 25.4556 28.768 26.9241L26.6407 29.0514Z" fill="#0131B8"/>
                                    </svg>
                                </div>

                                <Link href='/carrello' hrefLang={'it'} className="relative" aria-label="Vai al carrello" onClick={() => setMenuOpen(false)}>
                                    <div className="relative">
                                        <svg width="40" height="36" viewBox="0 0 40 36" fill="none" xmlns="http://www.w3.org/2000/svg">
                                            <path d="M0.629881 1.12204C0.780996 0.36646 1.51591 -0.12366 2.27148 0.0273119L4.22363 0.417937C6.42867 0.859011 8.08777 2.68818 8.31152 4.92575L8.46972 6.51169H33.7266C37.4181 6.51178 40.1268 9.98113 39.2314 13.5625L37.1426 21.9179C36.2626 25.4378 33.0998 27.9071 29.4717 27.9072H12.7412C10.1514 27.9072 7.96972 25.9717 7.66113 23.4004L5.82031 8.05954L5.53515 5.20309C5.43339 4.18612 4.67894 3.35478 3.67676 3.15427L1.72461 2.76364C0.968977 2.61251 0.47881 1.87767 0.629881 1.12204Z" fill="#0131B8"/>
                                            <circle cx="14.0905" cy="33.0234" r="2.79069" fill="#0131B8"/>
                                            <circle cx="30.8346" cy="33.0234" r="2.79069" fill="#0131B8"/>
                                        </svg>
                                        {isClient && cartCount > 0 && (
                                            <Badge className="absolute -top-2 -right-2 !px-2 text-white border-0">
                                                {cartCount}
                                            </Badge>
                                        )}
                                    </div>
                                </Link>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {leadFrom && (
                <DeferredFormLead
                    textarea={true}
                    articolo={''}
                    title={'Chatta con noi'}
                    marca={''}
                    modello={''}
                    setOpen={setLeadFrom}
                    open={leadFrom}
                    phone={telephone}
                    whatsappMessage="Salve, vorrei avere maggiori informazioni."
                />
            )}
        </>
    );
};

export default NavbarMobile;
